import React, { useEffect, useMemo, useState } from "react"
import { ExpandMore } from "@mui/icons-material"
import { InputField } from "../../../components/InputField"
import { useAuth } from "../../../contexts/AuthContext"
import { ENDPOINT } from "../../../constants"
import { KyzzenUser } from "../../../types"
import { useDebounceCallback } from "../../../hooks/useDebounce"
import { MiniDeLoading } from "../../../components/DeLoading"
import UserTable from "./user-table"
import UserDrawer from "./user-drawer"
import DeleteUserModal from "./DeleteUserModal"

enum ModalType {
  VIEW = "view",
  DELETE = "delete",
}

const UserManagement = () => {
  const { getUserJWT } = useAuth()

  const [searchText, setSearchText] = useState("")
  const [offset, setOffset] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [users, setUsers] = useState<KyzzenUser[]>([])
  const [hasMore, setHasMore] = useState(true)
  const [modal, setModal] = useState<ModalType | null>(null)
  const [user, setUser] = useState<KyzzenUser | null>(null)

  const firstLoad = useMemo(
    () => isLoading && offset === 0,
    [offset, isLoading]
  )
  const openModal = (type: ModalType, user: KyzzenUser) => {
    setUser(user)
    setModal(type)
  }
  const getKyzzenUsers = async (options: {
    offset: number
    search: string
    hasMore: boolean
  }) => {
    if (!options.hasMore) return []
    const userJWT = getUserJWT()
    const res = await fetch(
      `${ENDPOINT.KYZZEN_USERS}?limit=100&offset=${options.offset}&search=${options.search}`,
      {
        headers: {
          "Content-type": "application/json",
          Authorization: userJWT ? `Bearer ${userJWT}` : "",
        },
      }
    )
    const data = (await res.json()) as {
      pagination: { limit: number; offset: number; total: number }
      success: boolean
      data: KyzzenUser[]
      message: string
    }
    setHasMore(
      data.pagination.limit + data.pagination.offset < data.pagination.total
    )
    return data.data
  }
  const fetchMore = () => setOffset((prev) => prev + 100)
  const handleSearch = useDebounceCallback((text: string) => {
    setHasMore(true)
    setOffset(0)
    setSearchText(text)
  }, 300)

  const refetch = async () => {
    setIsLoading(true)
    try {
      const moreUsers = await getKyzzenUsers({
        offset,
        search: searchText,
        hasMore,
      })
      setUsers(offset > 0 ? [...users, ...moreUsers] : moreUsers)
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    refetch()
  }, [searchText, offset])

  return (
    <main className="flex flex-col pt-8 lg:pt-10 p-6 h-[calc(100vh_-_114px)] relative">
      <div className="mt-0 flex flex-row mb-4 justify-between items-center sm:flex-row">
        <h1 className="font-semibold text-3xl text-left">User Management</h1>
      </div>
      <div>
        <InputField
          type="search"
          onChange={(e) => handleSearch(e.target.value)}
          className="w-full mt-2 mb-1 mx-0 bg-gray-90 border-gray-80 rounded-full"
          placeholder="Search for Kyzzen username, email, discord, telegram or twitter handle"
        />
      </div>
      <UserTable
        users={users}
        isLoading={firstLoad}
        fetchMore={fetchMore}
        onViewUser={(user) => openModal(ModalType.VIEW, user)}
        onDeleteUser={(user) => openModal(ModalType.DELETE, user)}
      />
      {hasMore && !firstLoad && (
        <button
          className="w-10 h-10 min-h-10 min-w-10 rounded-full border border-kyzzen-secondary flex justify-center items-center text-xl mx-auto bg-kyzzen-primary z-10 absolute bottom-0 left-1/2 -translate-x-1/2"
          style={{
            boxShadow: "-7px -23px 22px 20px rgba(0, 0, 0, 0.06)",
          }}
          onClick={fetchMore}
        >
          {isLoading && offset !== 0 ? <MiniDeLoading /> : <ExpandMore />}
        </button>
      )}
      <UserDrawer
        isModalOpen={modal === ModalType.VIEW}
        user={user}
        closeModal={() => {
          setModal(null)
          setUser(null)
        }}
        onDeleteUser={(user) => openModal(ModalType.DELETE, user)}
      />
      <DeleteUserModal
        isModalOpen={modal === ModalType.DELETE}
        user={user}
        closeModal={() => {
          setModal(null)
          setUser(null)
        }}
        refetch={() => {
          setOffset(0)
          setSearchText("")
        }}
      />
    </main>
  )
}

export default UserManagement
